// Cap group fields for offer actions (capGroup / capGroupAmount on offerActionDetail redux-form).
import React, { useEffect, useState } from 'react';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Field, change } from 'redux-form';
import { renderCreatableSelect, RenderNumberPicker } from '../../../components/FormFields/reduxFormRenderers';
import api from '../../../services';
import { useDispatch, useSelector } from 'react-redux';
import { parseCapGroupAdditionalData, resolveCapGroup } from '../../../utils/CriteriaHelpers';

const LABEL_SX = { display: 'block', mb: 0.5 };

// Wraps renderCreatableSelect so redux-form onChange and handleSelectCapGroup both run (fill amount + Redis).
function CreatableSelectField({ input, meta, onChange, onCreate, ...rest }) {
  return renderCreatableSelect({
    ...rest,
    input: {
      ...input,
      onChange: (value) => {
        input.onChange(value);
        if (onChange) {
          onChange(value);
        }
      },
    },
    meta,
    onCreate,
  });
}

export default function CapGroupSection() {
  const dispatch = useDispatch();
  const [capGroups, setCapGroups] = useState([]);
  const [currentGroupCapAmount, setCurrentGroupCapAmount] = useState(0);

  // Load cap group options for the creatable select dropdown.
  useEffect(() => {
    api
      .get('cap-group')
      .then((res) =>
        setCapGroups(
          (res.data || []).map((g) => ({
            id: g.id,
            name: g.name,
            additionalData: parseCapGroupAdditionalData(g.additionalData),
          }))
        )
      )
      .catch(() => setCapGroups([]));
  }, []);
  const selectedCapGroup = useSelector((state) => state?.form?.offerActionDetail?.values?.capGroup);
  const selectedCapGroupId = selectedCapGroup?.id;

  // Refresh read-only Redis cap amount when the selected cap group changes.
  useEffect(() => {
    if (selectedCapGroupId && !selectedCapGroupId.toString().startsWith('temp-')) {
      api
        .get(`cap-group/current-group-cap-amount/${selectedCapGroupId}`)
        .then((res) => setCurrentGroupCapAmount(res.data ?? 0))
        .catch(() => setCurrentGroupCapAmount(0));
    } else {
      setCurrentGroupCapAmount(0);
    }
  }, [selectedCapGroupId]);

  const handleCreateCapGroup = (inputValue) => {
    const tempId = `temp-${Date.now()}`;

    dispatch(change('offerActionDetail', 'capGroup', { id: tempId, name: inputValue }));
    dispatch(change('offerActionDetail', 'capGroupAmount', ''));
    setCurrentGroupCapAmount(0);
  };

  const handleSelectCapGroup = async (capGroupValue) => {
    if (!capGroupValue) {
      dispatch(change('offerActionDetail', 'capGroup', null));
      dispatch(change('offerActionDetail', 'capGroupAmount', ''));
      setCurrentGroupCapAmount(0);
      return;
    }

    const selected = resolveCapGroup(capGroups, capGroupValue.id ?? capGroupValue.value);

    if (!selected) {
      return;
    }

    dispatch(change('offerActionDetail', 'capGroup', { id: selected.id, name: selected.name }));
    dispatch(change('offerActionDetail', 'capGroupAmount', selected.capAmount ?? ''));

    try {
      const redisRes = await api.get(`cap-group/current-group-cap-amount/${selected.id}`);
      setCurrentGroupCapAmount(redisRes.data ?? 0);
    } catch {
      setCurrentGroupCapAmount(0);
    }
  };

  // Rendered inside the criteria panel's own "Cap Group" section, which supplies the frame.
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, lg: 4 }}>
        <FormLabel sx={LABEL_SX}>Cap Group</FormLabel>
        <Field
          name='capGroup'
          component={CreatableSelectField}
          data={capGroups}
          textField='name'
          valueField='id'
          onCreate={handleCreateCapGroup}
          onChange={handleSelectCapGroup}
          placeholder='Select or create Cap Group'
        />
      </Grid>

      <Grid size={{ xs: 12, lg: 4 }}>
        {/* An IconButton so the explanation is reachable by keyboard; it used to hang off an
              `icon-info` glyph that this app has no stylesheet for, so nothing was drawn at all.
              describeChild keeps the button's own name instead of being replaced by the text. */}
        <Stack direction='row' spacing={0.5} sx={{ alignItems: 'center', mb: 0.5 }}>
          <FormLabel>Cap Amount</FormLabel>
          <Tooltip
            describeChild
            placement='top'
            title='Max successful posts per day across every offer action sharing this Cap Group. Each successful post counts once toward the limit; failed posts do not count and do not free up capacity. The counter resets daily. Once the limit is reached, all actions in this Cap Group are suppressed for the rest of the day.'
          >
            <IconButton size='small' aria-label='About Cap Amount'>
              <InfoOutlined fontSize='inherit' />
            </IconButton>
          </Tooltip>
        </Stack>
        <Field name='capGroupAmount' component={RenderNumberPicker} placeholder='Enter Cap Amount' />
      </Grid>

      <Grid size={{ xs: 12, lg: 4 }}>
        <FormLabel htmlFor='currentGroupCapAmount' sx={LABEL_SX}>
          Current Cap Amount (Redis)
        </FormLabel>
        <TextField
          id='currentGroupCapAmount'
          type='number'
          value={currentGroupCapAmount || 0}
          disabled
          slotProps={{ htmlInput: { readOnly: true } }}
        />
      </Grid>
    </Grid>
  );
}
